/**
 * Board layouts: the waypoint tour is the board's identity. Every layout must
 * satisfy the multi-pass property — consecutive waypoints sit on opposite
 * sides so each leg wants to cross the shared central build zone, making one
 * maze count for (nearly) every pass. That property is the genre's core payoff
 * (Pillar 1); the prototype's original tour ('current') violates it with two
 * full edge legs, which is why the candidates exist.
 *
 * Waypoints are balance data: they feed balanceHash, so each layout produces
 * distinct, self-describing replays. Switching layouts is a balance change and
 * re-records the golden corpus once a layout is locked (ADR to follow).
 */

export interface Waypoint {
  readonly x: number;
  readonly y: number;
  readonly label: string;
}

export interface BoardLayout {
  readonly key: string;
  readonly name: string;
  /** IN, 1..4, OUT — ground creeps visit in array order. */
  readonly waypoints: readonly Waypoint[];
  /** Flyer tour as indexes into waypoints (a permutation of 0..5). */
  readonly flyOrder: readonly number[];
}

export const BOARDS: readonly BoardLayout[] = [
  {
    // The prototype's v2.2 tour, kept as the comparison baseline. Two legs
    // (IN->1 left edge, 3->4 right edge) never touch the middle: a center
    // maze multiplies each stone across only 3 of 5 passes.
    key: 'current',
    name: 'Prototype tour',
    waypoints: [
      { x: 2, y: 2, label: 'IN' },
      { x: 2, y: 29, label: '1' },
      { x: 27, y: 16, label: '2' },
      { x: 53, y: 2, label: '3' },
      { x: 53, y: 29, label: '4' },
      { x: 27, y: 2, label: 'OUT' },
    ],
    flyOrder: [0, 4, 3, 1, 2, 5],
  },
  {
    // Faithful adaptation of the classic WC3 tour to 56x32: short opening
    // corridor along the top, then down the left-center, across the lower
    // band, up the right-center, and a long final diagonal through the
    // middle. The mid/lower center is the shared maze zone.
    key: 'classic',
    name: 'Classic tour',
    waypoints: [
      { x: 2, y: 2, label: 'IN' },
      { x: 20, y: 2, label: '1' },
      { x: 14, y: 26, label: '2' },
      { x: 42, y: 26, label: '3' },
      { x: 53, y: 2, label: '4' },
      { x: 27, y: 29, label: 'OUT' },
    ],
    flyOrder: [0, 3, 4, 2, 1, 5],
  },
  {
    // Designed for our landscape board: every one of the five legs crosses
    // the central third, and the exit sits at dead center — creeps sink into
    // the heart of the maze. The strongest multi-pass payoff of the three.
    key: 'rift',
    name: 'The Rift',
    waypoints: [
      { x: 2, y: 2, label: 'IN' },
      { x: 53, y: 16, label: '1' },
      { x: 2, y: 29, label: '2' },
      { x: 27, y: 2, label: '3' },
      { x: 53, y: 29, label: '4' },
      { x: 27, y: 16, label: 'OUT' },
    ],
    flyOrder: [0, 4, 1, 2, 3, 5],
  },
];

/**
 * The shipping layout: The Rift (ADR 0006, locked 2026-07-12 after the
 * owner's playtests -- including a wave-40 run that became a golden fixture).
 * 'current' and 'classic' remain for the legacy corpus and comparison.
 */
export const DEFAULT_BOARD_KEY = 'rift';

/**
 * balanceHashes of shipped historical balance versions, per board. Replays
 * carrying one of these hashes predate the endless/slates release (replay
 * v1/v2 era); the balance they were played under is byte-identical to today's
 * for waves 1-50 and they use no post-v2 ops, so they still re-simulate
 * bit-exactly -- only under the legacy end-at-50 rule (ADR 0007). This map
 * lets boardForReplay place them on the right board after the live hashes
 * moved. Append on every hash-moving release; never remove entries.
 */
export const HISTORICAL_HASHES: ReadonlyMap<number, string> = new Map([
  [2121747419, 'current'],
  [31969563, 'classic'],
  [829482235, 'rift'], // the live pre-endless leaderboard (family records)
]);
